import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface CustomerTypeLookup {
  id: number;
  name: string;
}

export interface CustomerAssigneeLookup {
  id: number;
  userName: string | null;
  displayName: string | null;
}

/** One row of a CRM catalogue a customer points at - a billing status, a
 *  pricing area, a communication preference. */
export interface CustomerCatalogLookup {
  id: number;
  name: string;
}

export interface StateLookup {
  id: number;
  code: string;
  label: string;
}

export interface LeadActionTypeLookup {
  id: number;
  code: string;
  label: string;
}

export interface LeadStageLookup {
  id: number;
  name: string;
  code: string;
  outcome: string;
  vocabulary: string;
  description: string | null;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export interface LeadAssigneeLookup {
  id: number;
  userName: string | null;
  displayName: string | null;
}

export interface LeadStageTransitionLookup {
  fromLeadStageId: number;
  toLeadStageId: number;
}

/** One system reporting interval. Pickers show the label; APIs and URLs carry the code. */
export interface ReportingIntervalLookup {
  id: number;
  code: string;
  label: string;
}

/** One business category a lead can be. The app-wide store_type vocabulary. */
export interface BusinessTypeLookup {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class LookupApiService {
  constructor(private readonly http: HttpClient) {}

  customerTypes(): Observable<CustomerTypeLookup[]> {
    return this.http.get<CustomerTypeLookup[]>('/api/lookup/customer-types');
  }

  customerSources(): Observable<string[]> {
    return this.http.get<string[]>('/api/lookup/customer-sources');
  }

  customerAssignees(): Observable<CustomerAssigneeLookup[]> {
    return this.http.get<CustomerAssigneeLookup[]>('/api/lookup/customer-assignees');
  }

  billingStatuses(): Observable<CustomerCatalogLookup[]> {
    return this.http.get<CustomerCatalogLookup[]>('/api/lookup/billing-statuses');
  }

  pricingAreas(): Observable<CustomerCatalogLookup[]> {
    return this.http.get<CustomerCatalogLookup[]>('/api/lookup/pricing-areas');
  }

  communicationPreferences(): Observable<CustomerCatalogLookup[]> {
    return this.http.get<CustomerCatalogLookup[]>('/api/lookup/communication-preferences');
  }

  states(): Observable<StateLookup[]> {
    return this.http.get<StateLookup[]>('/api/lookup/states');
  }

  leadActionTypes(): Observable<LeadActionTypeLookup[]> {
    return this.http.get<LeadActionTypeLookup[]>('/api/lookup/lead-action-types');
  }

  leadStages(): Observable<LeadStageLookup[]> {
    return this.http.get<LeadStageLookup[]>('/api/lookup/lead-stages');
  }

  leadAssignees(): Observable<LeadAssigneeLookup[]> {
    return this.http.get<LeadAssigneeLookup[]>('/api/lookup/lead-assignees');
  }

  leadStageTransitions(): Observable<LeadStageTransitionLookup[]> {
    return this.http.get<LeadStageTransitionLookup[]>('/api/lookup/lead-stage-transitions');
  }

  businessTypes(): Observable<BusinessTypeLookup[]> {
    return this.http.get<BusinessTypeLookup[]>('/api/lookup/business-types');
  }

  reportingIntervals(): Observable<ReportingIntervalLookup[]> {
    return this.http.get<ReportingIntervalLookup[]>('/api/lookup/reporting-intervals');
  }
}
